/**
 * Green Flag — PillTabs
 *
 * The Dating / Friends / Likes Me segmented tab row. Active tab is mint
 * (bg.brandSoft) with forest-ink bold label. Each tab has a matching icon.
 *
 * Implemented directly (not on Chakra's Tabs primitive) because the design
 * is a uniform segmented control — not a tab panel with content — and the
 * recipe maps 1:1 to these visual slots.
 *
 * Tabs can be conditionally hidden via `hiddenKeys` (e.g. "Likes Me" is
 * suppressed when nobody at the event has liked the viewer).
 */

import { Box, chakra, useSlotRecipe } from "@chakra-ui/react";
import { Icon, type IconName } from "../Icon/Icon";

export type PillTabKey = "dating" | "friends" | "likesMe";

const TABS: { key: PillTabKey; label: string; icon: IconName }[] = [
  { key: "dating",  label: "Dating",   icon: "heart"     },
  { key: "friends", label: "Friends",  icon: "smile"     },
  { key: "likesMe", label: "Likes Me", icon: "thumbs-up" },
];

export interface PillTabsProps {
  value: PillTabKey;
  onChange: (key: PillTabKey) => void;
  hiddenKeys?: PillTabKey[];
}

export function PillTabs({ value, onChange, hiddenKeys }: PillTabsProps) {
  const recipe = useSlotRecipe({ key: "pillTabs" });
  const rootStyles = recipe({} as Parameters<typeof recipe>[0]);
  const visibleTabs = hiddenKeys?.length
    ? TABS.filter((t) => !hiddenKeys.includes(t.key))
    : TABS;

  return (
    <Box css={rootStyles.root}>
      {visibleTabs.map((t) => {
        const active = value === t.key;
        const styles = recipe({ state: active ? "active" : "inactive" } as Parameters<typeof recipe>[0]);
        return (
          <chakra.button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            css={styles.tab}
            aria-pressed={active}
          >
            <Icon name={t.icon} boxSize="14px" />
            <chakra.span css={styles.tabLabel}>{t.label}</chakra.span>
          </chakra.button>
        );
      })}
    </Box>
  );
}
